import {
  EnrichedContact,
  extractAxiosErrorMessage,
  OutreachDraftResponse,
  OutreachInputItem,
  PeopleRole,
  useAsyncOperation,
  useLeadOutreachThreads,
  useOutreach,
  useOutreachTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { UserContext } from "../../auth/UserContext";
import CollapsibleCard from "../site/CollapsibleCard";

// The cold-outreach drafting studio. Opened from a pulled contact's "Draft email"
// button, it drafts a first email on open, then lets the operator iterate: untick
// facts in the "Data used" panel to leave them out, and / or type feedback, then
// Rerun to append a new turn to the same thread. Copy buttons hand off the subject +
// body; admins can open the full AI trace for any turn. Every turn's cost + trace is
// persisted server-side (visible on /costs and in Data & Config). The modal stays
// mounted (hidden when closed) so a drafted thread survives a close/reopen.
//
// Disclosure levels for the nearby-operational-site proximity fact (values must match
// the BE PROXIMITY_LEVELS). The default keeps the specific customer site unnamed - the
// operator can dial it up or down per draft.
const PROXIMITY_ITEM_ID = "proximity";
const PROXIMITY_LEVELS: { value: string; label: string }[] = [
  { value: "area_distance", label: "Distance + area, don't name it" },
  { value: "area_distance_size", label: "Include the site's size" },
  { value: "named", label: "Name the specific site + exact distance" },
  { value: "vague", label: "Keep it vague (no specifics)" },
];
const PROXIMITY_DEFAULT_LEVEL = "area_distance";

// Keep the studio calm: exactly two font sizes - a base for the title / subject / body,
// and a smaller one for every label, meta line, fact, and caption.
const FONT_LG = 14;
const FONT_SM = 12;

// Marks a fact we hold now that the draft on screen did not use. Same size as the fact
// it annotates, so it reads as an aside rather than a second column.
const NEW_FACT_ADORNER: React.CSSProperties = {
  color: "#0d6efd",
  marginLeft: 8,
  fontSize: FONT_SM,
};

// Facts distilled from the logged email thread. Each gets a link through to the full
// thread: the fact is a condensation, and an operator deciding whether a warm draft is
// right needs the actual exchange, not our summary of it. Mirrors the BE fact ids in
// outreach_input.py.
const THREAD_BACKED_FACTS = new Set(["relationship_last_email", "relationship_reply"]);

// The two links that hang off a thread-derived fact. Extracted because the fact list is
// rendered twice (toggleable + standard rows) and a second copy of this would drift - the
// same duplication that keeps breaking the lead surfaces.
//
// "View AI trace" is the summary's OWN trace, not the draft's: the fact is a model's
// reading of the thread, so the operator judging whether to trust it needs to see that
// call - and the raw thread it read - not the email that consumed it.
const ThreadFactLinks: React.FC<{
  itemId: string;
  onViewThread: () => void;
  summaryTraceRunId: string | null;
  onViewSummaryTrace: (runId: string) => void;
}> = ({ itemId, onViewThread, summaryTraceRunId, onViewSummaryTrace }) => {
  if (!THREAD_BACKED_FACTS.has(itemId)) return null;
  const linkStyle: React.CSSProperties = {
    fontSize: FONT_SM,
    verticalAlign: "baseline",
    marginLeft: 6,
  };
  return (
    <>
      <Button
        variant="link"
        className="p-0"
        style={linkStyle}
        onClick={onViewThread}
      >
        View thread
      </Button>
      {summaryTraceRunId && (
        <Button
          variant="link"
          className="p-0"
          style={linkStyle}
          onClick={() => onViewSummaryTrace(summaryTraceRunId)}
        >
          View AI trace
        </Button>
      )}
    </>
  );
};

const OutreachDraftModal: React.FC<{
  show: boolean;
  onHide: () => void;
  leadId: string;
  role: PeopleRole;
  contact: EnrichedContact | null;
  companyName: string | null;
  // Required, not optional: the relationship facts are a summary of a real thread, and
  // an operator judging a warm draft needs to be able to read what was actually said.
  onViewThread: () => void;
}> = ({ show, onHide, leadId, role, contact, companyName, onViewThread }) => {
  const { user } = useContext(UserContext);
  const { draftOutreach, loadOutreachLatestTurn } = useOutreach();
  const queryClient = useQueryClient();

  // Existing outreach threads for this lead (the same query that drives the "v2" badge -
  // React Query dedupes by key, so this is not a second request). We resolve whether THIS
  // contact already has a thread, so opening it loads the saved draft instead of writing a
  // brand-new one (which would spend a fresh AI call and start a duplicate thread).
  const threadsQuery = useLeadOutreachThreads(leadId, show);

  // Retain the last contact the modal was opened for, so the persisted draft stays
  // labelled after the parent clears `contact` on close and the closing fade doesn't
  // flash empty.
  const [shownContact, setShownContact] = useState<EnrichedContact | null>(
    contact
  );
  useEffect(() => {
    if (contact) setShownContact(contact);
  }, [contact]);
  const apolloId = shownContact?.apolloId ?? "";
  const contactName = shownContact?.name ?? "";

  // The saved thread for THIS contact (role + apolloId), or null if none yet. Drives the
  // load-vs-draft decision when the modal opens.
  const existingMessageId = useMemo(() => {
    for (const t of threadsQuery.data ?? []) {
      if (t.role === role && t.contactApolloId === apolloId) {
        return t.outreachMessageId;
      }
    }
    return null;
  }, [threadsQuery.data, role, apolloId]);

  const [draft, setDraft] = useState<OutreachDraftResponse | null>(null);
  // The ids the operator has unticked - the facts NOT to render into the next run.
  // Re-seeded from each response so a rerun and its result stay in sync.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<string>("");
  // How much of the nearby-operational-site data the email may use. Applied on the next
  // draft/rerun (like the fact checkboxes), default "don't name it".
  const [proximityLevel, setProximityLevel] = useState<string>(
    PROXIMITY_DEFAULT_LEVEL
  );
  const [error, setError] = useState<string | null>(null);
  // Which trace the viewer is showing, or null when closed. Not a boolean: the draft
  // and the thread summary are two separate model calls with two separate traces, and
  // the operator can open either.
  const [traceRunId, setTraceRunId] = useState<string | null>(null);
  const [copied, setCopied] = useState<"subject" | "body" | "email" | null>(
    null
  );
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The draft operation currently being polled, plus the context the result has to be
  // applied under. Null whenever nothing is in flight.
  const [draftOp, setDraftOp] = useState<{
    key: string;
    siteId: string;
    forApollo: string | null;
    rerun: boolean;
  } | null>(null);
  const draftOperation = useAsyncOperation({
    siteId: draftOp?.siteId ?? "",
    operationKey: draftOp?.key ?? null,
  });

  // In flight from the moment the kickoff POST leaves until the operation settles, so
  // the spinner and the disabled Rerun buttons cover the whole wait, not just the POST.
  const drafting = draftOutreach.isLoading || draftOp !== null;
  const loadingExisting = loadOutreachLatestTurn.isLoading;

  // The contact the open-effect last decided for. Its two jobs: skip re-deciding a
  // contact we've already handled (persists across close), and let an in-flight
  // draft/load bail if the operator has since switched to a different contact - so a
  // slow draft for contact C can't land under contact D's header.
  const handledApollo = useRef<string | null>(null);

  // Load the contact's saved thread (its latest turn) instead of drafting a new one - no
  // AI call, no new thread. Populates the modal exactly as a draft would.
  const loadExisting = async (messageId: string) => {
    const forApollo = apolloId;
    setError(null);
    try {
      const res = await loadOutreachLatestTurn.mutateAsync({
        leadId,
        outreachMessageId: messageId,
      });
      if (handledApollo.current !== forApollo) return; // switched contact mid-load
      setDraft(res);
      setExcluded(
        new Set(res.inputs.filter((i) => !i.included).map((i) => i.id))
      );
      setTraceRunId(null);
    } catch (e) {
      if (handledApollo.current !== forApollo) return;
      setError(
        (await extractAxiosErrorMessage(e)) ?? "Could not load the saved draft."
      );
    }
  };

  // Drafting is a long-running operation, not a request we can await: the CRM read plus
  // the model call measured 26-30s, which straddles the 29s API-Gateway ceiling and used
  // to surface as an unexplained "Service Unavailable". The POST starts the operation and
  // this holds the key we poll; `forApollo` carries the same switched-contact guard the
  // awaited version had, so a slow draft for contact C can't land under contact D.
  const runDraft = async (rerun: boolean) => {
    const forApollo = apolloId;
    setError(null);
    try {
      const kickoff = await draftOutreach.mutateAsync({
        leadId,
        body: {
          role,
          apolloId,
          userEmail: user?.email,
          outreachMessageId: rerun ? draft?.outreachMessageId : undefined,
          excludedInputIds: rerun ? [...excluded] : undefined,
          feedback: rerun ? feedback.trim() || undefined : undefined,
          proximityDisclosure: proximityLevel,
        },
      });
      if (handledApollo.current !== forApollo) return; // switched contact mid-draft
      setDraftOp({
        key: kickoff.operationKey,
        siteId: kickoff.siteId,
        forApollo,
        rerun,
      });
    } catch (e) {
      if (handledApollo.current !== forApollo) return;
      setError(
        (await extractAxiosErrorMessage(e)) ?? "Could not draft the email."
      );
    }
  };

  // Apply (or fail) the polled draft. Split from runDraft because the result no longer
  // arrives as the POST's return value.
  useEffect(() => {
    if (!draftOp) return;
    const data = draftOperation.data;
    if (draftOperation.timedOut) {
      setDraftOp(null);
      setError("Drafting timed out. Try again.");
      return;
    }
    if (!data) return;
    if (data.status === "completed") {
      const res = data.result as OutreachDraftResponse | undefined;
      const { forApollo, rerun } = draftOp;
      setDraftOp(null);
      if (!res) {
        setError("The draft finished but returned nothing. Try again.");
        return;
      }
      // The turn exists now, so the per-contact "v2" badge is stale until refreshed.
      // This used to ride on the mutation's onSuccess, which now fires at kickoff -
      // before there is any turn to count.
      void queryClient.invalidateQueries(["leadOutreachThreads", leadId]);
      if (handledApollo.current !== forApollo) return; // switched contact mid-draft
      setDraft(res);
      // Keep the checkboxes in step with the server's view of what was included.
      setExcluded(
        new Set(res.inputs.filter((i) => !i.included).map((i) => i.id))
      );
      // Feedback is one-shot: it applied to this turn, so clear it for the next.
      if (rerun) setFeedback("");
      setTraceRunId(null);
    } else if (data.status === "failed") {
      const { forApollo } = draftOp;
      setDraftOp(null);
      if (handledApollo.current !== forApollo) return;
      setError(data.error || "Could not draft the email.");
    }
  }, [
    draftOperation.data,
    draftOperation.timedOut,
    draftOp,
    leadId,
    queryClient,
  ]);

  // On open, decide ONCE per contact: load its saved thread if one exists, else draft a
  // fresh first email. We gate on the threads query having SUCCEEDED (not merely
  // "fetched" - isFetched flips true on error too, which would let us draft a fresh
  // duplicate thread while silently hiding the lookup failure). The ref persists across
  // close (the modal stays mounted), so reopening the same contact keeps whatever is
  // shown; opening a different contact re-decides.
  useEffect(() => {
    if (!show || !apolloId) return;
    if (!threadsQuery.isSuccess) return; // wait until we actually KNOW if a thread exists
    if (handledApollo.current === apolloId) return;
    handledApollo.current = apolloId;
    setDraft(null);
    setExcluded(new Set());
    setFeedback("");
    setProximityLevel(PROXIMITY_DEFAULT_LEVEL);
    setError(null);
    setTraceRunId(null);
    if (existingMessageId) void loadExisting(existingMessageId);
    else void runDraft(false);
    // Decide only when opening a not-yet-handled contact (once the threads have loaded) -
    // not when runDraft's / loadExisting's closure identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, apolloId, threadsQuery.isSuccess, existingMessageId]);

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    []
  );

  const copy = (kind: "subject" | "body" | "email", text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(kind);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), 1500);
  };

  const toggle = (id: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasNewInputs = (draft?.inputs ?? []).some((item) => item.isNew);

  // The relationship facts the BE resolved from a live CRM read. Their presence IS the
  // warm signal - there is no separate flag to keep in step with them.
  const relationshipItems = (draft?.inputs ?? []).filter(
    (item) => item.category === "Relationship"
  );
  const isWarm = relationshipItems.length > 0;

  // Group the facts by category for the inspector, preserving first-seen order.
  const grouped = useMemo(() => {
    const out: { category: string; items: OutreachInputItem[] }[] = [];
    for (const item of draft?.inputs ?? []) {
      let bucket = out.find((g) => g.category === item.category);
      if (!bucket) {
        bucket = { category: item.category, items: [] };
        out.push(bucket);
      }
      bucket.items.push(item);
    }
    return out;
  }, [draft?.inputs]);

  const traceQuery = useOutreachTrace(
    draft?.outreachMessageId ?? null,
    traceRunId,
    traceRunId !== null
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: FONT_LG }}>
          Draft outreach email
          {isWarm && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 999,
                padding: "2px 9px",
                background: "#d1e7dd",
                color: "#0f5132",
                verticalAlign: "middle",
              }}
              title="We have contacted this person before, so this is written as a follow-up"
            >
              Warm - previously contacted
            </span>
          )}
          <div style={{ fontSize: FONT_SM, color: "#888", fontWeight: 400 }}>
            To {contactName}
            {companyName ? ` at ${companyName}` : ""} ({role})
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {threadsQuery.isError && !draft ? (
          // We couldn't determine whether a saved thread exists, so we deliberately do
          // NOT auto-draft (that would risk a duplicate thread + a hidden failure).
          <Alert variant="danger" style={{ fontSize: FONT_SM }}>
            Couldn&rsquo;t check whether a draft already exists for this
            contact.{" "}
            <Button
              variant="link"
              size="sm"
              style={{ padding: 0, fontSize: FONT_SM }}
              onClick={() => void threadsQuery.refetch()}
            >
              Retry
            </Button>
          </Alert>
        ) : (drafting || loadingExisting || threadsQuery.isLoading) &&
          !draft ? (
          <div style={{ fontSize: FONT_SM, color: "#888", padding: "12px 0" }}>
            <Spinner as="span" size="sm" animation="border" />{" "}
            {loadingExisting
              ? "Loading saved draft..."
              : threadsQuery.isLoading
                ? "Preparing..."
                : "Writing the first draft..."}
          </div>
        ) : draft ? (
          <div className="d-flex flex-column gap-3">
            {/* Saved draft written before the facts moved --------------------- */}
            {draft.factsChanged && (
              <Alert
                variant="info"
                style={{ fontSize: FONT_SM, marginBottom: 0 }}
              >
                The facts for this lead have changed - see the data section.{" "}
                {/* A Bootstrap .btn is vertical-align: middle with its own line-height,
                    so inline next to a sentence it sits off the text baseline. */}
                <Button
                  variant="link"
                  style={{
                    padding: 0,
                    border: 0,
                    fontSize: FONT_SM,
                    lineHeight: "inherit",
                    verticalAlign: "baseline",
                  }}
                  disabled={drafting}
                  onClick={() => void runDraft(true)}
                >
                  Rerun to update.
                </Button>
              </Alert>
            )}

            {/* The generated email + copy actions ---------------------------- */}
            <div
              style={{
                border: "1px solid #dbe2ea",
                borderRadius: 6,
                padding: 12,
                background: "#fbfcfd",
              }}
            >
              {contact?.email && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: FONT_SM, color: "#8a94a0" }}>
                      EMAIL
                    </span>
                    <Button
                      size="sm"
                      variant="link"
                      style={{ fontSize: FONT_SM, padding: 0 }}
                      onClick={() => copy("email", contact.email ?? "")}
                    >
                      {copied === "email" ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <div
                    style={{
                      fontSize: FONT_LG,
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {contact.email}
                    {contact.emailSource === "hubspot" && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          borderRadius: 999,
                          padding: "1px 8px",
                          background: "#d1e7dd",
                          color: "#0f5132",
                        }}
                      >
                        From HubSpot
                      </span>
                    )}
                  </div>
                </>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: FONT_SM, color: "#8a94a0" }}>
                  SUBJECT
                </span>
                <Button
                  size="sm"
                  variant="link"
                  style={{ fontSize: FONT_SM, padding: 0 }}
                  onClick={() => copy("subject", draft.subject)}
                >
                  {copied === "subject" ? "Copied" : "Copy"}
                </Button>
              </div>
              <div
                style={{ fontSize: FONT_LG, fontWeight: 600, marginBottom: 10 }}
              >
                {draft.subject}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: FONT_SM, color: "#8a94a0" }}>
                  BODY
                </span>
                <Button
                  size="sm"
                  variant="link"
                  style={{ fontSize: FONT_SM, padding: 0 }}
                  onClick={() => copy("body", draft.body)}
                >
                  {copied === "body" ? "Copied" : "Copy"}
                </Button>
              </div>
              <div style={{ whiteSpace: "pre-wrap", fontSize: FONT_LG }}>
                {draft.body}
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: FONT_SM,
                  color: "#9aa2ad",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span>
                  Turn {draft.turnIndex + 1} · {draft.modelId} ·{" "}
                  {draft.inputTokens.toLocaleString()} in /{" "}
                  {draft.outputTokens.toLocaleString()} out tokens
                </span>
                {draft.traceRunId && (
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: FONT_SM, padding: 0 }}
                    onClick={() => setTraceRunId(draft.traceRunId)}
                  >
                    View AI trace
                  </Button>
                )}
              </div>
            </div>

            {/* Data used to write this (collapsed) --------------------------- */}
            <CollapsibleCard
              title={
                <span style={{ fontSize: FONT_LG }}>
                  Data used to write this
                </span>
              }
              initialOpen={hasNewInputs}
            >
              <div
                style={{ fontSize: FONT_SM, color: "#666", marginBottom: 8 }}
              >
                Every fact the writer was allowed to use. The recipient and
                company are always included; untick a site-opportunity or
                proximity fact to leave it out, then Rerun - the writer never
                invents facts, so a fact it never sees cannot appear.
              </div>
              {grouped.length === 0 ? (
                <div style={{ fontSize: FONT_SM, color: "#888" }}>
                  No specific facts were available for this contact - the email
                  is qualitative.
                </div>
              ) : (
                grouped.map((g) => (
                  <div key={g.category} style={{ marginBottom: 8 }}>
                    <div
                      className="text-uppercase text-muted"
                      style={{ fontSize: FONT_SM, letterSpacing: 0.5 }}
                    >
                      {g.category}
                    </div>
                    {g.items.map((item) =>
                      item.toggleable ? (
                        <div key={item.id}>
                          <Form.Check
                            type="checkbox"
                            id={`outreach-input-${item.id}`}
                            checked={!excluded.has(item.id)}
                            onChange={() => toggle(item.id)}
                            label={
                              <span style={{ fontSize: FONT_SM }}>
                                <strong>{item.label}:</strong>{" "}
                                <span style={{ whiteSpace: "pre-wrap" }}>{item.value}</span>
                                <ThreadFactLinks
                                  itemId={item.id}
                                  onViewThread={onViewThread}
                                  summaryTraceRunId={
                                    draft.threadSummaryTraceRunId ?? null
                                  }
                                  onViewSummaryTrace={setTraceRunId}
                                />
                                {item.isNew && (
                                  <span style={NEW_FACT_ADORNER}>
                                    {excluded.has(item.id)
                                      ? "New - won't be used on next run"
                                      : "New - rerun to use"}
                                  </span>
                                )}
                              </span>
                            }
                          />
                          {item.id === PROXIMITY_ITEM_ID && (
                            <Form.Select
                              size="sm"
                              value={proximityLevel}
                              disabled={excluded.has(item.id)}
                              onChange={(e) =>
                                setProximityLevel(e.target.value)
                              }
                              style={{
                                fontSize: FONT_SM,
                                width: "auto",
                                marginTop: 4,
                                marginLeft: "1.5em",
                              }}
                              aria-label="How much of the nearby site to use"
                            >
                              {PROXIMITY_LEVELS.map((lvl) => (
                                <option key={lvl.value} value={lvl.value}>
                                  {lvl.label}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        </div>
                      ) : (
                        // Standard fact: always included, no checkbox. Indented to
                        // line up with the toggleable rows' labels.
                        <div
                          key={item.id}
                          style={{
                            fontSize: FONT_SM,
                            paddingLeft: "1.5em",
                            marginBottom: 2,
                          }}
                        >
                          <strong>{item.label}:</strong>{" "}
                          <span style={{ whiteSpace: "pre-wrap" }}>{item.value}</span>
                          <ThreadFactLinks
                            itemId={item.id}
                            onViewThread={onViewThread}
                            summaryTraceRunId={
                              draft.threadSummaryTraceRunId ?? null
                            }
                            onViewSummaryTrace={setTraceRunId}
                          />
                          {/* A standard fact is always included, so it can only ever
                              be waiting on a rerun. */}
                          {item.isNew && (
                            <span style={NEW_FACT_ADORNER}>
                              New - rerun to use
                            </span>
                          )}
                        </div>
                      )
                    )}
                  </div>
                ))
              )}
            </CollapsibleCard>

            {/* Feedback + Rerun --------------------------------------------- */}
            <div>
              <Form.Label style={{ fontSize: FONT_SM, marginBottom: 2 }}>
                Feedback for the next draft (optional)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={feedback}
                placeholder="e.g. shorter, warmer, lead with the nearby site, drop the stats"
                style={{ fontSize: FONT_SM }}
                onChange={(e) => setFeedback(e.target.value)}
              />
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginTop: 6,
                }}
              >
                <Button
                  variant="primary"
                  disabled={drafting}
                  onClick={() => void runDraft(true)}
                >
                  {drafting ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" />{" "}
                      Rewriting...
                    </>
                  ) : (
                    "Rerun with changes"
                  )}
                </Button>
                <span style={{ fontSize: FONT_SM, color: "#9aa2ad" }}>
                  Each rerun keeps the thread - every turn is saved with its
                  cost and trace.
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </Modal.Body>

      <Modal
        show={traceRunId !== null}
        onHide={() => setTraceRunId(null)}
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: FONT_LG }}>
            Outreach draft - AI trace
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {traceQuery.isLoading ? (
            <div style={{ fontSize: FONT_SM, color: "#888" }}>
              Loading trace...
            </div>
          ) : traceQuery.data ? (
            <AITraceViewer trace={traceQuery.data} />
          ) : (
            <div style={{ fontSize: FONT_SM, color: "#888" }}>
              No AI trace available for this run.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Modal>
  );
};

export default OutreachDraftModal;
